import logger from '@libs/log';
import { translate } from '@state/utils/helper';
import { useEffect } from 'react';
import { HiOutlineExternalLink } from 'react-icons/hi';
import { SiGrammarly } from 'react-icons/si';
import { toast } from 'sonner';

const GRAMMARLY_WARNING_DISMISSED_KEY = 'grammarlyWarningDismissed';
const GRAMMARLY_INSTRUCTIONS_URL =
  'https://support.grammarly.com/hc/en-us/articles/115000091632-Turn-off-the-Grammarly-browser-extension';

/**
 * Detects if the Grammarly browser extension is installed
 * Supports Chromium (Chrome, Edge), Firefox, and Safari
 * @returns {boolean} true if Grammarly extension is detected
 */
const isGrammarlyInstalled = (): boolean => {
  // Check for window.grammarly (common across browsers)
  if (typeof window !== 'undefined') {
    const windowWithGrammarly = window as Window & { grammarly?: unknown };
    if (windowWithGrammarly.grammarly) {
      return true;
    }
  }

  // Check for Grammarly-injected DOM elements
  // Grammarly typically injects elements with data-grammarly-shadow-root attribute
  if (typeof document !== 'undefined') {
    // Check for the grammarly-desktop-integration element (most reliable indicator)
    const grammarlyDesktopIntegration = document.querySelector('grammarly-desktop-integration');
    if (grammarlyDesktopIntegration) {
      return true;
    }

    // Check for shadow root containers (backup method)
    const grammarlyElements = document.querySelectorAll('[data-grammarly-shadow-root]');
    if (grammarlyElements.length > 0) {
      return true;
    }

    // Check for Grammarly extension ID in injected scripts (Chromium)
    const scripts = document.querySelectorAll('script');
    for (const script of scripts) {
      if (script.src.includes('grammarly')) {
        return true;
      }
    }

    // Check for Grammarly extension attributes on body or html
    if (
      document.body.hasAttribute('data-grammarly') ||
      document.documentElement.hasAttribute('data-grammarly')
    ) {
      return true;
    }
  }

  // Note: chrome.runtime.getURL() cannot detect other extensions as it only works
  // in the context of the calling extension. We rely on DOM/window/script detection above.

  return false;
};

/**
 * Hook to detect Grammarly extension and show warning toast
 * Checks on mount and shows a persistent warning toast if Grammarly is detected
 * and user hasn't dismissed it before
 */
export const useGrammarlyDetection = (): void => {
  useEffect(() => {
    // Check for Grammarly extension on app load
    const checkGrammarly = () => {
      // Check if user has already dismissed the warning
      let hasDismissed = false;
      try {
        hasDismissed = localStorage.getItem(GRAMMARLY_WARNING_DISMISSED_KEY) === 'true';
      } catch (error) {
        // localStorage not available (private browsing, storage full, etc.)
        logger.warn(`Failed to check Grammarly warning dismissal status:`, error);
      }

      if (!hasDismissed && isGrammarlyInstalled()) {
        const toastId = toast.warning(
          <div>
            <h3 className="grammarly-warning-title">
              <SiGrammarly />
              <span>{translate('grammarlyWarning.title')}</span>
            </h3>
            <p className="grammarly-warning-message">{translate('grammarlyWarning.message')}</p>
            <div className="grammarly-warning-buttons">
              <button
                className="grammarly-warning-button"
                onClick={() => {
                  window.open(GRAMMARLY_INSTRUCTIONS_URL, '_blank', 'noopener,noreferrer');
                }}
              >
                <span>{translate('grammarlyWarning.learnMore')}</span> <HiOutlineExternalLink />
              </button>
              <button
                className="grammarly-warning-dismiss-button"
                onClick={() => {
                  toast.dismiss(toastId);
                  try {
                    localStorage.setItem(GRAMMARLY_WARNING_DISMISSED_KEY, 'true');
                  } catch (error) {
                    logger.warn(`Failed to save Grammarly warning dismissal:`, error);
                  }
                }}
              >
                {translate('grammarlyWarning.dismiss')}
              </button>
            </div>
          </div>,
          {
            duration: Infinity,
            onDismiss: () => {
              try {
                localStorage.setItem(GRAMMARLY_WARNING_DISMISSED_KEY, 'true');
              } catch (error) {
                logger.warn(`Failed to save Grammarly warning dismissal:`, error);
              }
            },
          }
        );
      }
    };

    // Run check after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(checkGrammarly, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);
};
